/**
 * The COMPACT copy of one transcript artifact, pinned to the top of the chat scroll viewport once its inline
 * card has scrolled past (ACT2-538 — otherwise the route into a dashboard or audience is lost a couple of
 * turns after it was created).
 *
 * The one place that maps a {@link ChatArtifact} back to its type's card, so the pinned bar and the inline
 * card are always the same component in two variants and cannot drift apart.
 */
import type { ChatArtifact } from '../../lib/chat-artifacts';
import type { DashboardArtifactOpenHint } from '../../lib/dashboard-draft';
import { ChatAudienceArtifact } from './ChatAudienceArtifact';
import { DashboardArtifactCard } from './DashboardArtifactCard';

export interface PinnedArtifactCardProps {
  artifact: ChatArtifact;
  /** Lets a pinned DRAFT dashboard fall back to opening the in-chat preview, since it has no page yet. */
  onDashboardOpen?: (slug: string, hint?: DashboardArtifactOpenHint) => void;
}

export function PinnedArtifactCard({ artifact, onDashboardOpen }: PinnedArtifactCardProps): React.ReactElement {
  switch (artifact.type) {
    case 'audience':
      return <ChatAudienceArtifact artifact={artifact} variant="compact" />;
    case 'dashboard':
      return (
        <DashboardArtifactCard
          title={artifact.title}
          slug={artifact.slug}
          saved={artifact.saved}
          isDraft={artifact.isDraft}
          // Carried on the artifact, computed the same way the inline card computes it — hardcoding `true`
          // here would let the pinned copy advertise a preview the inline card correctly withheld, and the
          // click would dead-end in `setActive`'s "unknown slug" guard.
          previewAvailable={artifact.previewAvailable}
          variant="compact"
          // Only a click target when the host actually wired an open handler — otherwise omit onOpen so
          // the card renders inert rather than advertising a preview affordance that does nothing.
          {...(onDashboardOpen && {
            onOpen: () =>
              onDashboardOpen(artifact.slug, {
                title: artifact.title,
                saved: artifact.saved,
                ...(artifact.dashboardId === undefined ? {} : { dashboardId: artifact.dashboardId }),
              }),
          })}
        />
      );
  }
}
